import { Button, Tooltip } from '@mantine/core'
import type { ButtonProps } from '@mantine/core'
import type { ReactNode } from 'react'

type TooltipButtonProps = Omit<ButtonProps, 'disabled' | 'onClick' | 'children'> & {
  /**
   * Visually disables the button while keeping it hoverable so the tooltip can
   * still appear. Uses Mantine's `data-disabled` instead of the native
   * `disabled` attribute, which removes pointer events and suppresses tooltips.
   */
  disabled: boolean
  /** Message shown on hover while disabled. Omit/null for no tooltip. */
  disabledReason?: string | null
  onClick: () => void
  children: ReactNode
}

/**
 * A teal action button (Review/Submit order) that shows a hover tooltip
 * explaining why it is disabled. Shared across every ordering flow so the
 * disabled-button behaviour stays identical.
 */
export function TooltipButton({
  disabled,
  disabledReason,
  onClick,
  children,
  ...buttonProps
}: TooltipButtonProps) {
  const button = (
    <Button
      {...buttonProps}
      onClick={disabled ? undefined : onClick}
      data-disabled={disabled || undefined}
    >
      {children}
    </Button>
  )
  return disabled && disabledReason ? <Tooltip label={disabledReason}>{button}</Tooltip> : button
}
